import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  type UseFloatingReturn,
  type UseInteractionsReturn,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';

import type { PopoverProps } from './types';

const emptyListRef = {
  current: [],
};

interface UsePopoverReturn extends UseFloatingReturn, UseInteractionsReturn {
  open: boolean;
  setOpen: (open: boolean) => void;
  listContext: {
    elementsRef: React.MutableRefObject<Array<HTMLElement | null>>;
    activeIndex: number | null;
    setActiveIndex: (index: number | null) => void;
    selectedIndex: number | null;
    setSelectedIndex: (index: number | null) => void;
    handleSelect: (index: number) => void;
  };
  modal?: boolean;
  labelId?: string;
  descriptionId?: string;
  setLabelId: (id: string | undefined) => void;
  setDescriptionId: (id: string | undefined) => void;
}

export type { UsePopoverReturn };

export function usePopover({
  initialOpen = false,
  placement = 'bottom',
  strategy,
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  autoUpdate: autoPosition = true,
  middleware = [offset(5)],
  list,
  useDismissProps,
  onSelectListItem,
  openChangeCallback,
  trigger = 'click',
}: PopoverProps = {}): UsePopoverReturn {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  const listElementsRef = useRef<Array<HTMLElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(0);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  useEffect(() => {
    if (openChangeCallback) {
      openChangeCallback(open);
    }
  }, [open, openChangeCallback]);

  const data = useFloating({
    placement,
    strategy,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoPosition ? autoUpdate : undefined,
    middleware: [
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'end',
        padding: 5,
        fallbackPlacements: ['left', 'right'],
      }),
      shift({ padding: 24 }),
      ...middleware,
    ],
  });

  const context = data.context;

  const click = useClick(context, {
    enabled: trigger === 'click' && controlledOpen == null,
  });
  const hover = useHover(context, {
    enabled: trigger === 'hover',
    delay: { open: 150, close: 150 },
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
    outsidePress: true,
    ...useDismissProps,
  });
  const role = useRole(context);

  const listNav = useListNavigation(context, {
    listRef: list ? listElementsRef : emptyListRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
  });

  const interactions = useInteractions([click, hover, dismiss, role, listNav]);

  const handleSelectListItem = useCallback(
    (index: number) => {
      setSelectedIndex(index);
      onSelectListItem?.(index);
    },
    [onSelectListItem],
  );

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      listContext: {
        elementsRef: listElementsRef,
        activeIndex,
        setActiveIndex,
        selectedIndex,
        setSelectedIndex,
        handleSelect: handleSelectListItem,
      },
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
    }),
    [
      open,
      setOpen,
      interactions,
      data,
      modal,
      labelId,
      descriptionId,
      activeIndex,
      selectedIndex,
      handleSelectListItem,
    ],
  );
}
